import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = window.location.origin;

export const usePushNotifications = (user) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  // false ile başlat
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState('default');
  const [checking, setChecking] = useState(true);  // İlk kontrol için ayrı state

  useEffect(() => {
    const checkNotificationSupport = async () => {
      try {
        setChecking(true);
        
        // Browser desteğini kontrol et
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          setError('Bu tarayıcı push bildirimleri desteklemiyor');
          setChecking(false);
          return;
        }

        // Mevcut izin durumunu kontrol et
        if ('Notification' in window) {
          setPermission(Notification.permission);
        }

        // Service Worker'ın kayıtlı olup olmadığını kontrol et
        const registration = await navigator.serviceWorker.ready;

        // Mevcut browser subscription'ını kontrol et
        const browserSubscription = await registration.pushManager.getSubscription();
        
        if (browserSubscription) {
          console.log('✅ [Push] Browser\'da subscription bulundu, backend kontrol ediliyor...');
          
          // Backend'de de bu subscription kayıtlı mı kontrol et
          try {
            const response = await axios.get(`${API_URL}/api/push/subscription-status`, {
              params: { endpoint: browserSubscription.endpoint },
              withCredentials: true
            });
            
            if (response.data.exists) {
              console.log('✅ [Push] Backend\'de de kayıtlı');
              setIsSubscribed(true);
            } else {
              console.log('⚠️ [Push] Backend\'de kayıt yok, browser subscription temizleniyor...');
              await browserSubscription.unsubscribe();
              setIsSubscribed(false);
            }
          } catch (err) {
            console.warn('⚠️ [Push] Backend kontrolü başarısız, browser subscription temizleniyor...');
            await browserSubscription.unsubscribe();
            setIsSubscribed(false);
          }
        } else {
          console.log('ℹ️ [Push] Subscription yok');
          setIsSubscribed(false);
        }

        setChecking(false);
      } catch (err) {
        console.error('❌ [Push] Kurulum hatası:', err);
        setError(err.message);
        setChecking(false);
      }
    };

    if (user) {
      checkNotificationSupport();
    } else {
      setChecking(false);
    }
  }, [user]);

  // Base64 string'i Uint8Array'e dönüştür
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Güvenlik timeout - 30 saniye sonra otomatik reset
      const timeoutId = setTimeout(() => {
        console.error('⏱️ [Push] Timeout! İşlem çok uzun sürdü');
        setIsLoading(false);
        setError('İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.');
      }, 30000);

      console.log('🔔 [Push] 1. İzin isteniyor...');
      // Bildirim izni iste
      const perm = await Notification.requestPermission();
      setPermission(perm);
      console.log('🔔 [Push] 2. İzin durumu:', perm);

      if (perm !== 'granted') {
        clearTimeout(timeoutId);
        setIsLoading(false);
        throw new Error('Bildirim izni reddedildi. Lütfen tarayıcı ayarlarından izin verin.');
      }

      console.log('🔔 [Push] 3. Service Worker bekleniyor...');
      // Service Worker'ı hazırla
      const registration = await navigator.serviceWorker.ready;
      console.log('🔔 [Push] 4. Service Worker hazır');

      // Eski subscription varsa önce temizle
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('🔔 [Push] 4.5. Eski subscription bulundu, temizleniyor...');
        try {
          // Backend'den sil
          await axios.post(
            `${API_URL}/api/push/unsubscribe`,
            null,
            {
              params: { endpoint: existingSubscription.endpoint },
              withCredentials: true
            }
          );
          // Browser'dan sil
          await existingSubscription.unsubscribe();
          console.log('🔔 [Push] 4.6. Eski subscription temizlendi');
        } catch (err) {
          console.warn('⚠️ [Push] Eski subscription temizlenemedi:', err.message);
        }
      }

      console.log('🔔 [Push] 5. VAPID key alınıyor...');
      // VAPID public key'i backend'den al
      const keyResponse = await axios.get(`${API_URL}/api/push/vapid-public-key`);
      const publicKey = keyResponse.data.publicKey;
      console.log('🔔 [Push] 6. VAPID key alındı:', publicKey.substring(0, 20) + '...');

      console.log('🔔 [Push] 7. Push Manager subscription...');
      // Push Manager'a abone ol
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      console.log('🔔 [Push] 8. Subscription başarılı, endpoint:', subscription.endpoint);

      console.log('🔔 [Push] 9. Backend\'e kaydediliyor...');
      // Subscription'ı backend'e kaydet
      const response = await axios.post(
        `${API_URL}/api/push/subscribe`,
        subscription.toJSON(),
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      console.log('🔔 [Push] 10. Backend kaydı başarılı:', response.data);

      clearTimeout(timeoutId);
      setIsSubscribed(true);
      setIsLoading(false);
      
      return { success: true, message: 'Bildirimler başarıyla aktif edildi!' };

    } catch (err) {
      console.error('❌ [Push] Abonelik hatası:', err);
      console.error('❌ [Push] Hata detayı:', err.response?.data);
      console.error('❌ [Push] Hata status:', err.response?.status);
      const errorMessage = err.response?.data?.detail || err.message || 'Bildirimler aktif edilemedi';
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, message: errorMessage };
    } finally {
      // Her durumda loading'i kapat
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    try {
      console.log('🚨 [Push] UNSUBSCRIBE ÇAĞRILDI! Stack trace:');
      console.trace();
      
      setIsLoading(true);
      setError(null);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Backend'e abonelik iptali bildir
        await axios.post(
          `${API_URL}/api/push/unsubscribe`,
          null,
          {
            params: {
              endpoint: subscription.endpoint
            },
            withCredentials: true
          }
        );

        // Local subscription'ı iptal et
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      setIsLoading(false);
      
      return { success: true, message: 'Bildirimler devre dışı bırakıldı' };

    } catch (err) {
      console.error('Abonelik iptali hatası:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'İşlem başarısız oldu';
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, message: errorMessage };
    }
  };

  return {
    isSubscribed,
    isLoading,
    error,
    permission,
    subscribe,
    unsubscribe
  };
};
